import { useState, memo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Button, TableSortLabel, TextField, Typography } from '@mui/material';
import { BaseSort } from '~/components/common';
import { SearchIcon, SortTableIcon } from '~/assets/icons';
import { handleGetPriceItems } from '~/thunks/price-item/priceItemsThunk';
import { IPriceItemList } from '~/utils/interface/priceItem';
import { cast, handleApiError } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IResponseEstimateItem, IResponseGetEstimate } from '~/utils/interface/estimate';
import AddPriceItemDialog from './add-price-item-dialog/AddPriceItemDialog';
import { IResponseGetEstimateTemplate } from '~/utils/interface/estimate-template';
import PriceItem from './PriceItem';
import { handleGetPriceComponents } from '~/thunks/price-components/priceComponentsThunk';
import { handleGetPrices } from '~/thunks/price/pricesThunk';
import { FIXED_VALUE } from '~/utils/constants/common';
import MenuSortDirection from '~/components/common/base-table/menu-sort-direction/MenuSortDirection';
import { SortDirection } from '~/utils/types/common';
import { useParams } from 'react-router-dom';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import { priceItemActions } from '~/thunks/price-item/priceItemsSlice';

type PriceListItemProps = {
  form: any;
  editData?: IResponseGetEstimate | undefined | IResponseGetEstimateTemplate;
  companyTax: number;
};

function PriceListItem(props: PriceListItemProps) {
  //#region Destructuring Props
  const { form, editData, companyTax } = props;
  const { id } = useParams();

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State

  const { priceItems, filter } = useAppSelector((state) => state.priceItem);
  const { areaScopes } = useAppSelector((state) => state.areaScope);
  const [openAddPriceItemDialog, setOpenAddPriceItemDialog] = useState<boolean>(false);
  const defaultQuantity = useRef<number>(1);
  const [searchInput, setSearchInput] = useState<string>('');
  const [estimateItems, setEstimateItems] = useState<any>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    if (editData?.estimateItems) {
      const items = editData.estimateItems.map((data) => {
        let mapItem: IResponseEstimateItem = {
          id: data.id,
          priceItemId: data.priceItemId,
          priceItemCode: data.priceItemCode,
          priceItemName: data.priceItemName,
          description: data.description,
          location: data.location,
          unitPrice: Number(data.unitPrice ?? 0),
          quantity: Number(data.quantity ?? defaultQuantity.current),
          taxPercent: Number(data.taxPercent ?? 0),
          unit: data.unit,
          name: data.priceItemName,
        };
        mapItem.amount = calculateItemAmount(mapItem);

        return mapItem;
      });
      setEstimateItems(items);
      form.setFieldValue('estimateItems', items);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  useEffect(() => {
    dispatch(handleGetPriceItems({ ...filter, limit: 30, searchFields: ['code', 'title', 'description'] }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  useEffect(() => {
    if (id) {
      dispatch(handleGetAreaScopeList({ jobId: id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, id]);
  useEffect(() => {
    dispatch(handleGetPrices({ page: 0, limit: 0 }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetPriceComponents({ page: 0, limit: 0 }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);

  useEffect(() => {
    if (form.values.estimateItems) {
      handlePaymentCaculation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.estimateItems, priceItems]);

  useEffect(() => {
    if (form.values?.estimateItems?.length) {
      const estimateItems = form.values.estimateItems.map((estimateItem: IResponseEstimateItem) => {
        let mapItem: IResponseEstimateItem = {
          ...estimateItem,
          taxPercent: props.companyTax || 0,
        };
        mapItem.amount = calculateItemAmount(mapItem);
        return mapItem;
      });

      setEstimateItems(estimateItems);
      form.setFieldValue(`estimateItems`, estimateItems);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.companyTax]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddPriceItem = () => {
    setOpenAddPriceItemDialog(true);
  };

  const handleOpenAddPriceItemDialog = (value: boolean) => {
    setOpenAddPriceItemDialog(value);
  };

  const calculateItemAmount = (item: IResponseEstimateItem) => {
    const subtotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 1);
    return subtotal + (subtotal * (item?.taxPercent || 0)) / 100;
  };

  const handlePaymentCaculation = () => {
    let subTotal = 0;
    let total = 0;

    form.values.estimateItems.forEach((item: IResponseEstimateItem) => {
      subTotal += (Number(item.unitPrice) ?? 0) * (Number(item.quantity) ?? 0);
      total += Number(item.amount) || 0;
    });

    form.setFieldValue('subtotal', parseFloat(subTotal.toFixed(FIXED_VALUE)));
    form.setFieldValue('total', parseFloat(total.toFixed(FIXED_VALUE)));
  };

  const handleSelectedPriceItems = (priceItems: IPriceItemList[]) => {
    const selectedItems = priceItems.map((item: IPriceItemList) => {
      let mapItem: IResponseEstimateItem = {
        name: item.title,
        priceItemId: cast(item.id),
        priceItemCode: cast(item.code),
        priceItemName: cast(item.title),
        unitPrice: Number(item.unitPrice),
        location: cast(item.location),
        quantity: Number(item.quantity ?? defaultQuantity.current),
        taxPercent: Number(companyTax),
        description: cast(item.description),
        unit: cast(item.unit),
      };

      mapItem.amount = calculateItemAmount(mapItem);
      return mapItem;
    });
    const updatedEstimateItems = [...form.values.estimateItems, ...selectedItems];
    setEstimateItems(updatedEstimateItems);
    form.setFieldValue('estimateItems', updatedEstimateItems);
  };

  //#endregion Handle Function
  const openSortDirectionMenu = (property: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedProperty(property);
  };

  const handleCloseSort = () => {
    setAnchorEl(null);
  };
  const renderActiveSort = () => {
    return sortDirection;
  };
  const createSortHandler = (sortDirection: SortDirection) => {
    if (selectedProperty) {
      handleRequestSort(selectedProperty, sortDirection);
      setSortDirection(sortDirection);
    }
    handleCloseSort();
  };
  const handleRequestSort = (orderBy: string, order: SortDirection) => {
    const updatedEstimateItems = [...estimateItems].sort((a: any, b: any) => {
      if (orderBy === 'location') {
        const areaScopeA = areaScopes.find((scope) => scope.id === a.location)?.name ?? '';
        const areaScopeB = areaScopes.find((scope) => scope.id === b.location)?.name ?? '';
        if (order === 'ASC') {
          return areaScopeA > areaScopeB ? 1 : -1;
        } else {
          return areaScopeA < areaScopeB ? 1 : -1;
        }
      } else {
        if (order === 'ASC') {
          return a[orderBy] > b[orderBy] ? 1 : -1;
        } else {
          return a[orderBy] < b[orderBy] ? 1 : -1;
        }
      }
    });
    updatedEstimateItems.forEach((item, index) => {
      item.position = index + 1;
    });
    setEstimateItems(updatedEstimateItems);
    form.setFieldValue('estimateItems', updatedEstimateItems);
  };

  const handleSearchPriceListItem = async (text?: string) => {
    dispatch(
      priceItemActions.setFilter({
        search: text,
      })
    );
  };

  return (
    <Box>
      <Box className='bg-[#F3F9FF] w-full my-4 p-5 font-[600] text-[14px] border-y-2 border-solid border-y-[#e5eaef]'>
        {t('estimate.priceListItem.title')}
      </Box>
      <Box className='p-5'>
        <Box className='flex ml-auto w-[250px] mb-[10px]'>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('searchPlaceholder', { ns: 'admin/common' })}
            InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
          />
        </Box>
        <Box className={'flex justify-between items-center gap-[20px] my-3 w-full bg-[#ffffff]'}>
          <Box className={'w-[10px]'}></Box>

          <Box className='gap-[20px] w-full flex'>
            <Box className='flex-[0_0_10%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('priceItemName')}
              >
                {t('estimate.priceListItem.name')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_7%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('priceItemCode')}
              >
                {t('estimate.priceListItem.code')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_10%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('quantity')}
              >
                {t('estimate.priceListItem.quantity')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_13%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('description')}
              >
                {t('estimate.priceListItem.description')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_10%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('taxPercent')}
              >
                {t('estimate.priceListItem.taxPercent')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_10%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('unitPrice')}
              >
                {t('estimate.priceListItem.unitPrice')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_15%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('unit')}
              >
                {t('estimate.priceListItem.unit')}
              </TableSortLabel>
            </Box>
            <Box className='flex-[0_0_15%]'>
              <TableSortLabel
                IconComponent={() => <SortTableIcon className='ml-[20px] w-[19px] h-[19px]' />}
                onClick={openSortDirectionMenu('location')}
              >
                {t('estimate.priceListItem.area')}
              </TableSortLabel>
            </Box>
          </Box>
        </Box>
        <Box>
          <BaseSort
            data={estimateItems}
            onItemChange={(item) => {
              form.setFieldValue('estimateItems', item.data);
            }}
            search={(data) =>
              !searchInput || (data.priceItemName ?? '').toLowerCase().indexOf(searchInput.toLowerCase()) > -1
            }
            render={(data, index, onItemChange) => {
              return <PriceItem data={data} index={index} onItemChange={onItemChange} priceListItems={estimateItems} />;
            }}
          />
        </Box>
        <AddPriceItemDialog
          open={openAddPriceItemDialog}
          setOpen={handleOpenAddPriceItemDialog}
          selectedPriceItems={handleSelectedPriceItems}
          priceListItems={priceItems}
          form={form}
          onSearchChange={handleSearchPriceListItem}
        />
        <Typography className='text-red-600 text-[12px]'>
          {typeof form.errors?.estimateItems === 'string' ? form.errors.estimateItems : ''}
        </Typography>
        <Button className='mt-2' onClick={handleAddPriceItem}>
          {t('estimate.priceListItem.addNew')}
        </Button>
      </Box>
      <MenuSortDirection
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSort}
        active={renderActiveSort()}
        onSelectAscDirection={() => createSortHandler('ASC')}
        onSelectDescDirection={() => createSortHandler('DESC')}
      />
    </Box>
  );
}

export default memo(PriceListItem);
